import { LocationVarInheritance, LocationVarKey, MergedLocationVar } from "@swivl-mono/types";
import { ReactNode, createContext, useCallback, useContext, useMemo } from "react";

export interface TopicDraftEntry {
  source?: string;
  value?: string;
  /**
   * A staged revert: Save deletes the override row at this scope instead of
   * upserting `value`. `value` still carries the destination (parent/default)
   * value so rows display what the field will become. Editing the field again
   * clears the flag — the new value supersedes the revert.
   */
  revert?: boolean;
  /** Where a staged revert lands once saved — the pill rows show while the revert is staged. */
  revertInheritance?: LocationVarInheritance;
}
export type TopicDraft = Record<string, TopicDraftEntry>;
export type TopicErrors = Record<string, string>;

export interface TopicContextValue {
  /** Merged values for every var in the topic (keyed by varKey). */
  merged: Record<string, MergedLocationVar>;
  /**
   * The parent scope's merged values, used only for "Reverts to: …" tooltips.
   * Undefined at org scope, where a revert lands on the VarDef default instead.
   */
  parentMerged?: Record<string, MergedLocationVar>;
  /** Draft entries the operator has staged but not yet saved. */
  draft: TopicDraft;
  /** Per-key error messages from the last save attempt. */
  errors: TopicErrors;
  /** True when Swivl staff should see the LLM-normalized `value` under a collapsible. */
  isSwivlStaff: boolean;
  /**
   * True once the operator has entered edit mode for the topic. Rows render
   * their value as plain text until then. Always true for auto-save topics,
   * which have no explicit edit step.
   */
  isEditing: boolean;
  /**
   * True when an explicit Edit button gates this topic (i.e. not an auto-save
   * topic). Rows then unlock together on Edit and a change is what creates the
   * override, matching the Policies card. Auto-save topics keep the per-row
   * Customize gate, since they have no edit step to hang it on.
   */
  explicitEditing: boolean;
  /**
   * Enter or leave edit mode. Topics that manage their own per-section edit
   * affordances call this so the worksheet's save bar stays in sync.
   */
  setEditing: (next: boolean) => void;
  /** A save is in flight. */
  saving: boolean;
  /** How many fields have staged edits. */
  dirtyCount: number;
  /** Persist every staged edit, then leave edit mode. */
  saveTopic: () => Promise<void>;
  /** Drop every staged edit and leave edit mode. */
  cancelEdits: () => void;
  /** When defined (auto-save topics), rows flash a "Saved" indicator next to their title. */
  savedVisible?: boolean;

  /** Stage a policy-source change (raw prose). */
  onPolicySource: (key: LocationVarKey, next: string) => void;
  /** Stage a typed-setting / structured-var value change. */
  onSettingValue: (key: LocationVarKey, next: string) => void;
  /** Begin overriding the var at the current scope, seeding the draft with `seed`. */
  onCustomize: (key: LocationVarKey, seed: string, isPolicy: boolean) => void;
  /** Drop the staged draft for a key (revert local edits). */
  onDiscard: (key: LocationVarKey) => void;
  /**
   * Revert the override row for this var, restoring inheritance. On topics with
   * an explicit Edit step the revert is staged like any other edit — the row is
   * deleted on Save, and Cancel discards it. Auto-save topics have no Save step
   * to defer to, so they delete the row immediately.
   */
  onRevert: (row: MergedLocationVar) => void | Promise<void>;
}

const TopicContext = createContext<TopicContextValue | null>(null);

interface TopicProviderProps extends TopicContextValue {
  children: ReactNode;
}

export function TopicProvider({ children, ...value }: TopicProviderProps): React.ReactElement {
  return <TopicContext.Provider value={value}>{children}</TopicContext.Provider>;
}

export function useTopicContext(): TopicContextValue {
  const ctx = useContext(TopicContext);
  if (!ctx) throw new Error("useTopicContext must be used inside <TopicProvider>");
  return ctx;
}

/**
 * Helper for topic-page row components: returns the per-key state that a row
 * needs to render itself (merged value, draft entry, error message, plus
 * convenience flags).
 */
export function useTopicRow(varKey: LocationVarKey): {
  merged: MergedLocationVar;
  draft: TopicDraftEntry | undefined;
  error: string | undefined;
} {
  const ctx = useTopicContext();
  const merged = useMemo<MergedLocationVar>(
    () => ctx.merged[varKey] ?? { key: varKey, value: "", source: null, inheritance: LocationVarInheritance.DEFAULT },
    [ctx.merged, varKey]
  );
  const draft = ctx.draft[varKey];
  const error = ctx.errors[varKey];
  return { merged, draft, error };
}

/**
 * Bound handlers for a specific varKey - saves topic pages from re-deriving the
 * curry pattern in every row.
 */
export function useTopicHandlers(varKey: LocationVarKey): {
  onPolicySource: (next: string) => void;
  onSettingValue: (next: string) => void;
  onCustomize: (seed: string, isPolicy: boolean) => void;
  onDiscard: () => void;
  onRevert: (row: MergedLocationVar) => void | Promise<void>;
} {
  const ctx = useTopicContext();
  const onPolicySource = useCallback((next: string) => ctx.onPolicySource(varKey, next), [ctx, varKey]);
  const onSettingValue = useCallback((next: string) => ctx.onSettingValue(varKey, next), [ctx, varKey]);
  const onCustomize = useCallback((seed: string, isPolicy: boolean) => ctx.onCustomize(varKey, seed, isPolicy), [ctx, varKey]);
  const onDiscard = useCallback(() => ctx.onDiscard(varKey), [ctx, varKey]);
  const onRevert = useCallback((row: MergedLocationVar) => ctx.onRevert(row), [ctx]);
  return { onPolicySource, onSettingValue, onCustomize, onDiscard, onRevert };
}
